import { useAuth } from '../components/auth/AuthProvider'

export interface AuditLogEntry {
  tabName: string
  action: string
  details?: Record<string, any>
  targetUserId?: string
  targetUserEmail?: string
  ipAddress?: string
  userAgent?: string
}

export interface DatabaseAuditEntry {
  user_email: string
  tab_name: string
  action: string
  details: Record<string, any> | null
}

class AuditLogService {
  private static instance: AuditLogService
  
  private constructor() {}
  
  public static getInstance(): AuditLogService {
    if (!AuditLogService.instance) {
      AuditLogService.instance = new AuditLogService()
    }
    return AuditLogService.instance
  }

  /**
   * Log an audit event to the database
   */
  async logEvent(entry: AuditLogEntry, targetUserEmail?: string): Promise<void> {
    try {
      const auditEntry: DatabaseAuditEntry = {
        user_email: entry.targetUserEmail || targetUserEmail || '',
        tab_name: entry.tabName,
        action: entry.action,
        details: entry.details || null
      }

      const response = await fetch('/api/audit-log', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(auditEntry)
      })

      if (!response.ok) {
        throw new Error(`Failed to log audit event: ${response.statusText}`)
      }

      // Log to console in development for debugging
      if (process.env.NODE_ENV === 'development') {
        console.log('Audit Log:', auditEntry)
      }
    } catch (error) {
      console.error('Error logging audit event:', error)
      // Don't throw the error to avoid breaking the UI
      // Audit logging should be non-blocking
    }
  }

  /**
   * Get audit logs with optional filters
   */
  async getAuditLogs(filters?: {
    userEmail?: string
    targetUserEmail?: string
    tabName?: string
    action?: string
    startDate?: Date
    endDate?: Date
    limit?: number
    offset?: number
  }): Promise<any[]> {
    try {
      const queryParams = new URLSearchParams()
      
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined) {
            if (value instanceof Date) {
              queryParams.append(key, value.toISOString())
            } else {
              queryParams.append(key, String(value))
            }
          }
        })
      }

      const response = await fetch(`/api/audit-log?${queryParams}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch audit logs: ${response.statusText}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Error fetching audit logs:', error)
      return []
    }
  }
}

// Hook for easy access to audit logging in components
export const useAuditLog = () => {
  const { user } = useAuth()
  const auditService = AuditLogService.getInstance()

  const logEvent = async (entry: AuditLogEntry, targetUserEmail?: string) => {
    if (user) {
      await auditService.logEvent(entry, targetUserEmail)
    }
  }

  const getAuditLogs = async (filters?: Parameters<typeof auditService.getAuditLogs>[0]) => {
    return auditService.getAuditLogs(filters)
  }

  return {
    logEvent,
    getAuditLogs
  }
}

export default AuditLogService

// Common audit actions - use these constants for consistency
export const AUDIT_ACTIONS = {
  // User Profile Actions
  USER_SEARCHED: 'USER_SEARCHED',
  USER_PROFILE_VIEWED: 'USER_PROFILE_VIEWED',
  USER_PROFILE_CREATED: 'USER_PROFILE_CREATED',
  
  // The Academy Actions
  POINTS_DEPOSITED: 'POINTS_DEPOSITED',
  POINTS_SPENT: 'POINTS_SPENT',
  POINTS_SUBTRACTED: 'POINTS_SUBTRACTED',
  OPT_IN_STATUS_UPDATED: 'OPT_IN_STATUS_UPDATED',
  TIER_QUALIFYING_RUN: 'TIER_QUALIFYING_RUN',
  TIER_PROMOTED: 'TIER_PROMOTED',
  TIER_DEMOTED: 'TIER_DEMOTED',
  ACADEMY_ENROLLMENT: 'ACADEMY_ENROLLMENT',
  
  // Offers Actions
  OFFER_ISSUED: 'OFFER_ISSUED',
  OFFERS_VIEWED: 'OFFERS_VIEWED',
  GIFT_CARD_ISSUED: 'GIFT_CARD_ISSUED',
  
  // Help Tickets Actions
  HELP_TICKETS_VIEWED: 'HELP_TICKETS_VIEWED',
  
  // Customer Merge Actions (Super Admin only)
  CUSTOMER_MERGE_INITIATED: 'CUSTOMER_MERGE_INITIATED',
  CUSTOMER_MERGE_COMPLETED: 'CUSTOMER_MERGE_COMPLETED',
  CUSTOMER_FORGOTTEN: 'CUSTOMER_FORGOTTEN',
  
  // Authentication Actions
  USER_LOGIN: 'USER_LOGIN',
  USER_LOGOUT: 'USER_LOGOUT',

  // User Management Actions (Admin Portal access)
  USER_LOOKUP: 'USER_LOOKUP',
  USER_GROUP_ADDED: 'USER_GROUP_ADDED',
  USER_GROUP_REMOVED: 'USER_GROUP_REMOVED',

  // General Actions
  TAB_VIEWED: 'TAB_VIEWED'
} as const

export type AuditAction = typeof AUDIT_ACTIONS[keyof typeof AUDIT_ACTIONS]
